import { Column, Row, Text } from "@/components";
import { FinishPumpkin } from "@/components/leaderboard/FinishPumpkin";
import { FinishSpiderWeb } from "@/components/leaderboard/FinishSpiderWeb";
import { RacetrackPetRacer } from "@/components/leaderboard/RacetrackPetRacer";
import type { RacetrackGoalScope } from "@/lib/leaderboardGoal";
import { racetrackLanes } from "@/lib/racetrackLanes";
import { teamGoals } from "@/lib/teamGoals";
import { cn } from "@/lib/utils";

import type { LeaderboardUser, Pet } from "@/types";

/* Constants */

// Repeating white dashes drawn as an inline SVG, tiled across each lane.
const LANE_DASH_PATTERN = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="3" viewBox="0 0 18 3"><rect width="6" height="3" rx="1.5" fill="white" fill-opacity="0.7"/></svg>',
)}")`;

const LANE_GAP_CLASS = "gap-3";

// Pet position along the lane, scaled to the finish line: 0 revenue sits at the
// start, hitting the finish reaches the flag. Clamped so beating it doesn't
// overshoot.
const LANE_PROGRESS_MIN_PERCENT = 7;
const LANE_PROGRESS_MAX_PERCENT = 100;

// Once the leader passes the goal, the finish line extends to the leader's
// revenue rounded up to the next step, so the race keeps going.
const REVENUE_FINISH_STEP = 5000;

const progressForRevenue = (revenue: number, target: number): number => {
  if (target <= 0) return LANE_PROGRESS_MIN_PERCENT;
  const ratio = Math.min(revenue / target, 1);
  return (
    LANE_PROGRESS_MIN_PERCENT +
    ratio * (LANE_PROGRESS_MAX_PERCENT - LANE_PROGRESS_MIN_PERCENT)
  );
};

/**
 * Revenue track: one labeled lane per racer, each with the staffer's pet
 * positioned by revenue. Last month's winner wears their pet's crown topper.
 */
export const RevenueRacetrack = ({
  users,
  revenueGoalPerStaffer,
  goalScope,
  isHalloween = false,
  petByUserId,
  inactiveUserIds,
  className,
}: {
  users: LeaderboardUser[];
  revenueGoalPerStaffer?: number;
  goalScope?: RacetrackGoalScope | null;
  isHalloween?: boolean;
  petByUserId?: Map<number, Pet>;
  inactiveUserIds?: Set<number>;
  className?: string;
}) => {
  /* Helpers */
  // Lane order = last month's revenue ranking, so lanes stay put as the viewed
  // date range changes. Pet position still reflects the selected period.
  const lanes = [...users].sort(
    (a, b) =>
      b.previousMonthRevenue - a.previousMonthRevenue || b.revenue - a.revenue,
  );
  const maxRevenue = Math.max(...lanes.map((u) => u.revenue), 0);
  // The goal is the finish line until the leader passes it; then the finish
  // extends to the leader's revenue rounded up to the next step, and the goal
  // becomes an intermediate marker so leaders can keep racing past it.
  const passedGoal =
    revenueGoalPerStaffer !== undefined && maxRevenue > revenueGoalPerStaffer;
  // With no goal set, fall back to a leader-relative race.
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const baseTarget = revenueGoalPerStaffer || maxRevenue;
  const trackTarget = passedGoal
    ? Math.ceil(maxRevenue / REVENUE_FINISH_STEP) * REVENUE_FINISH_STEP
    : baseTarget;
  // Where the original goal sits on the extended track (null until it's passed).
  const goalMarkerPercent = passedGoal
    ? progressForRevenue(revenueGoalPerStaffer, trackTarget)
    : null;
  // On-track marker label, e.g. "Weekly Goal $16K" (only set once passed).
  const goalMarkerLabel = passedGoal
    ? `${goalScope === "WEEK" ? "Weekly" : "Monthly"} Goal ${teamGoals.formatRevenueGoal(revenueGoalPerStaffer)}`
    : "";
  const laneHeightStyle = { height: racetrackLanes.PET_HEIGHT_PX };

  // Crossed the line: hit the goal, or lead a goal-less race.
  const hasFinished = (user: LeaderboardUser): boolean => {
    if (revenueGoalPerStaffer) return user.revenue >= revenueGoalPerStaffer;
    return maxRevenue > 0 && user.revenue === maxRevenue;
  };
  // The spider drops onto the leader's lane once the leader finishes.
  const leaderLaneIndex = lanes.findIndex(
    (u) => u.revenue === maxRevenue && hasFinished(u),
  );

  /* Render */
  // Top-right marker. Normally the weekly/monthly goal; once the goal is passed,
  // the finish line is the rounded-up ceiling, so it shows that instead (the goal
  // itself moves to the on-track dashed marker).
  const renderFinishGoal = () => {
    if (!revenueGoalPerStaffer) return null;

    return (
      <Row
        className={cn(
          "items-center justify-end gap-1.5",
          // Leave room for the spider web hanging over the finish line.
          isHalloween && "pr-14",
        )}
      >
        <Text className="text-xs font-medium uppercase tracking-wider text-slate-300">
          {passedGoal
            ? "Finish"
            : `${goalScope === "WEEK" ? "Weekly" : "Monthly"} Goal`}
        </Text>
        <Text className="text-sm font-semibold text-white">
          {teamGoals.formatRevenueGoal(
            passedGoal ? trackTarget : revenueGoalPerStaffer,
          )}
        </Text>
      </Row>
    );
  };

  const renderLane = (user: LeaderboardUser, laneIndex: number) => (
    <Row
      key={user.userId}
      className="relative items-center gap-2 overflow-visible"
      style={{ ...laneHeightStyle, zIndex: laneIndex + 1 }}
    >
      <Row
        className={cn(
          "relative min-w-0 flex-1 items-center overflow-visible",
          "min-h-[28px]",
          !isHalloween && "bg-repeat-x",
        )}
        style={
          isHalloween
            ? undefined
            : {
                backgroundImage: LANE_DASH_PATTERN,
                backgroundPosition: "center",
              }
        }
      >
        {/* Keep lanes visible without competing with the backdrop. */}
        {isHalloween && (
          <Column className="pointer-events-none absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-white/25" />
        )}
        {goalMarkerPercent !== null && (
          <div
            className="pointer-events-none absolute bottom-0 top-0 z-0 border-l-2 border-dashed border-white"
            style={{ left: `${goalMarkerPercent.toString()}%` }}
          >
            {laneIndex === 0 && (
              <Text className="absolute -top-5 left-0 -translate-x-1/2 whitespace-nowrap text-[10px] font-semibold uppercase tracking-wider text-white">
                {goalMarkerLabel}
              </Text>
            )}
          </div>
        )}
        <RacetrackPetRacer
          progressPercent={progressForRevenue(user.revenue, trackTarget)}
          laneZIndex={laneIndex + 1}
          pet={petByUserId?.get(user.userId)}
        />
      </Row>
      <Row className="w-8 shrink-0 justify-center">
        {isHalloween ? (
          <FinishPumpkin isLit={hasFinished(user)} />
        ) : (
          <Text className="text-[22.5px] leading-none">🏁</Text>
        )}
      </Row>
    </Row>
  );

  const render = () => {
    if (lanes.length === 0) return null;

    return (
      <Column className={cn("min-w-0 flex-1 gap-2", className)}>
        {renderFinishGoal()}
        <Row className="min-w-0 flex-1 items-stretch gap-3 overflow-visible py-1">
          <Column
            className={cn(
              "w-max shrink-0 justify-center text-right",
              LANE_GAP_CLASS,
            )}
          >
            {lanes.map((user) => (
              <Row
                key={user.userId}
                className="min-w-0 items-center justify-end gap-1 whitespace-nowrap"
                style={laneHeightStyle}
              >
                <Text
                  truncate
                  className="text-xs font-medium leading-none text-slate-200"
                >
                  {user.firstName}
                </Text>
                {inactiveUserIds?.has(user.userId) && (
                  <Text className="shrink-0 text-xs font-normal leading-none text-slate-400">
                    (inactive)
                  </Text>
                )}
              </Row>
            ))}
          </Column>
          <Column
            className={cn(
              "relative min-w-0 flex-1 justify-center overflow-visible",
              LANE_GAP_CLASS,
            )}
          >
            {isHalloween ? (
              <FinishSpiderWeb
                laneCount={lanes.length}
                spiderLaneIndex={
                  leaderLaneIndex === -1 ? null : leaderLaneIndex
                }
              />
            ) : null}
            {lanes.map(renderLane)}
          </Column>
        </Row>
      </Column>
    );
  };

  return render();
};
